import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import HistoryIcon from '@mui/icons-material/History';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { ToastContainer, toast } from 'react-toastify';
import { fetchMisSolicitudes, crearSolicitud, actualizarSolicitud, eliminarSolicitud } from '../../peticiones_API/PeticionesVacaciones';
import { fetchFestivosCombinados, fetchFestivosByCalendario, fetchFestivosByCalendarioColab } from '../../peticiones_API/PeticionesFestivos';
import { fetchPeriodosVerano } from '../../peticiones_API/PeticionesPeriodosVerano';
import { fetchPeriodosVeranoColab } from '../../peticiones_API/PeticionesPeriodosVeranoColab';
import { ConfirmacionEliminar } from '../elementos/eventos/avisos/VentanaEmergente';
import { UsuarioContext } from '../providers/personaContext';
import './GestorMisSolicitudes.css';

function GestorMisSolicitudes() {
  const navigate = useNavigate();
  const { persona } = useContext(UsuarioContext);
  const esColab = persona?.es_colaborador || false;
  const [solicitudes, setSolicitudes] = useState([]);
  const [anioSeleccionado, setAnioSeleccionado] = useState(new Date().getFullYear());
  const [aniosDisponibles, setAniosDisponibles] = useState([new Date().getFullYear()]);
  const [resumen, setResumen] = useState({
    dias_totales: 0,
    dias_usados: 0,
    dias_disponibles: 0,
    dias_verano_total: 0,
    dias_verano_usados: 0,
    dias_pendientes_anio_anterior: 0,
    dentro_periodo_gracia: false,
    fecha_limite_dias_pendientes: '',
    tiene_calendario_laboral: true,
    calendario_laboral_id: null
  });
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [solicitudEditando, setSolicitudEditando] = useState(null);
  const [festivos, setFestivos] = useState([]);
  const [periodosVerano, setPeriodosVerano] = useState([]);
  const [nuevaSolicitud, setNuevaSolicitud] = useState({
    fecha_inicio: '',
    fecha_fin: '',
    usar_dias_anio_anterior: false,
    comentarios: ''
  });

  const confirmDialog = ConfirmacionEliminar();

  const cargarSolicitudes = useCallback(async (anio) => {
    try {
      setLoading(true);
      const data = await fetchMisSolicitudes(anio);
      setSolicitudes(data.solicitudes || []);
      if (data.resumen) {
        setResumen(data.resumen);
        if (data.resumen.anios_disponibles) {
          setAniosDisponibles(data.resumen.anios_disponibles);
        }
      }
    } catch (error) {
      console.error('Error al cargar solicitudes:', error);
      toast.error('Error al cargar las solicitudes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargarSolicitudes(anioSeleccionado);
  }, [anioSeleccionado, cargarSolicitudes]);

  useEffect(() => {
    const cargarFestivos = async () => {
      try {
        const anio = new Date().getFullYear();
        let data;
        if (resumen.calendario_laboral_colab_id) {
          data = await fetchFestivosByCalendarioColab(resumen.calendario_laboral_colab_id, anio);
        } else if (resumen.calendario_laboral_id) {
          data = await fetchFestivosByCalendario(resumen.calendario_laboral_id, anio);
        } else {
          data = await fetchFestivosCombinados(anio);
        }
        const todasFechas = [
          ...(data.nacional || []).map(f => f.fecha),
          ...(data.empresa || []).map(f => f.fecha),
          ...(data.provincial || []).map(f => f.fecha),
          ...(data.municipal || []).map(f => f.fecha)
        ];
        setFestivos(todasFechas);
      } catch (error) {
        console.error('Error al cargar festivos:', error);
      }
    };
    cargarFestivos();
  }, [resumen.calendario_laboral_id, resumen.calendario_laboral_colab_id, esColab]);

  useEffect(() => {
    const cargarPeriodosVerano = async () => {
      try {
        const tieneCalColab = persona?.calendario_laboral_colab_id;
        const data = tieneCalColab ? await fetchPeriodosVeranoColab() : await fetchPeriodosVerano();
        setPeriodosVerano(data || []);
      } catch (error) {
        console.error('Error al cargar periodos de verano:', error);
      }
    };
    cargarPeriodosVerano();
  }, []);

  const handleCrearSolicitud = async (e) => {
    e.preventDefault();
    try {
      const diasCalculados = calcularDias();
      if (diasCalculados <= 0) {
        toast.error('Los días seleccionados son festivos o fines de semana y no cuentan como días laborables');
        return;
      }

      if (nuevaSolicitud.usar_dias_anio_anterior) {
        if (diasCalculados > resumen.dias_pendientes_anio_anterior) {
          toast.error(`Solo tienes ${resumen.dias_pendientes_anio_anterior} días disponibles del año anterior`);
          return;
        }
      }

      await crearSolicitud({
        ...nuevaSolicitud,
        es_periodo_verano: esPeriodoVerano(),
        dias_solicitados: diasCalculados
      });
      toast.success('Solicitud creada correctamente');
      handleCerrarModal();
      cargarSolicitudes(anioSeleccionado);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al crear la solicitud');
    }
  };

  const getEstadoChip = (estado) => {
    const config = {
      pendiente: { color: 'warning', label: 'Pendiente' },
      aprobada: { color: 'success', label: 'Aprobada' },
      rechazada: { color: 'error', label: 'Rechazada' },
      cancelada: { color: 'default', label: 'Cancelada' }
    };
    const { color, label } = config[estado] || { color: 'default', label: estado };
    return <Chip label={label} color={color} size="small" />;
  };

  const puedeEditar = (solicitud) => {
    if (solicitud.estado === 'rechazada') return false;
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const fechaInicio = new Date(solicitud.fecha_inicio);
    fechaInicio.setHours(0, 0, 0, 0);
    return fechaInicio >= hoy;
  };

  const puedeEliminar = (solicitud) => {
    if (solicitud.estado === 'rechazada') return false;
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const fechaInicio = new Date(solicitud.fecha_inicio);
    fechaInicio.setHours(0, 0, 0, 0);
    return fechaInicio >= hoy && solicitud.estado === 'pendiente';
  };

  const handleEditar = (solicitud) => {
    setModoEdicion(true);
    setSolicitudEditando(solicitud);
    setNuevaSolicitud({
      fecha_inicio: solicitud.fecha_inicio,
      fecha_fin: solicitud.fecha_fin,
      usar_dias_anio_anterior: solicitud.usar_dias_anio_anterior || false,
      comentarios: solicitud.comentarios || ''
    });
    setShowModal(true);
  };

  const handleEliminar = async (solicitud) => {
    const confirmado = await confirmDialog.showAlert('eliminar', 'solicitud de vacaciones');
    if (confirmado) {
      try {
        await eliminarSolicitud(solicitud.Id);
        toast.success('Solicitud eliminada correctamente');
        cargarSolicitudes(anioSeleccionado);
      } catch (error) {
        toast.error(error.response?.data?.message || 'Error al eliminar la solicitud');
      }
    }
  };

  const handleCerrarModal = () => {
    setShowModal(false);
    setModoEdicion(false);
    setSolicitudEditando(null);
    setNuevaSolicitud({
      fecha_inicio: '',
      fecha_fin: '',
      usar_dias_anio_anterior: false,
      comentarios: ''
    });
  };

  const handleGuardarEdicion = async (e) => {
    e.preventDefault();
    try {
      const diasCalculados = calcularDias();
      if (diasCalculados <= 0) {
        toast.error('Los días seleccionados son festivos o fines de semana y no cuentan como días laborables');
        return;
      }

      const response = await actualizarSolicitud(solicitudEditando.Id, {
        ...nuevaSolicitud,
        es_periodo_verano: esPeriodoVerano(),
        dias_solicitados: diasCalculados
      });

      if (response.mensaje) {
        toast.info(response.mensaje);
      } else {
        toast.success('Solicitud actualizada correctamente');
      }

      handleCerrarModal();
      cargarSolicitudes(anioSeleccionado);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al actualizar la solicitud');
    }
  };

  const calcularDias = () => {
    if (!nuevaSolicitud.fecha_inicio || !nuevaSolicitud.fecha_fin) return 0;
    const inicio = new Date(nuevaSolicitud.fecha_inicio);
    const fin = new Date(nuevaSolicitud.fecha_fin);
    let dias = 0;
    const actual = new Date(inicio);
    while (actual <= fin) {
      const esFinDeSemana = actual.getDay() === 0 || actual.getDay() === 6;
      const fechaStr = actual.toISOString().split('T')[0];
      const esFestivo = festivos.includes(fechaStr);
      if (!esFinDeSemana && !esFestivo) {
        dias++;
      }
      actual.setDate(actual.getDate() + 1);
    }
    return dias;
  };

  const calcularDiasDesglose = () => {
    if (!nuevaSolicitud.fecha_inicio || !nuevaSolicitud.fecha_fin) return { normales: 0, verano: 0, total: 0 };
    const inicio = new Date(nuevaSolicitud.fecha_inicio);
    const fin = new Date(nuevaSolicitud.fecha_fin);
    let normales = 0;
    let verano = 0;
    const actual = new Date(inicio);
    while (actual <= fin) {
      const esFinDeSemana = actual.getDay() === 0 || actual.getDay() === 6;
      const fechaStr = actual.toISOString().split('T')[0];
      const esFestivo = festivos.includes(fechaStr);
      if (!esFinDeSemana && !esFestivo) {
        const enVerano = periodosVerano.some(p => {
          const pInicio = p.fecha_inicio?.split('T')[0];
          const pFin = p.fecha_fin?.split('T')[0];
          return pInicio && pFin && fechaStr >= pInicio && fechaStr <= pFin;
        });
        if (enVerano) verano++;
        else normales++;
      }
      actual.setDate(actual.getDate() + 1);
    }
    return { normales, verano, total: normales + verano };
  };

  const esPeriodoVerano = () => {
    if (!nuevaSolicitud.fecha_inicio || !nuevaSolicitud.fecha_fin || periodosVerano.length === 0) return false;

    const fechaInicio = new Date(nuevaSolicitud.fecha_inicio);
    const fechaFin = new Date(nuevaSolicitud.fecha_fin);

    return periodosVerano.some(periodo => {
      const periodoInicio = new Date(periodo.fecha_inicio);
      const periodoFin = new Date(periodo.fecha_fin);

      return fechaInicio <= periodoFin && fechaFin >= periodoInicio;
    });
  };

  const getFechaMinFin = () => {
    if (!nuevaSolicitud.fecha_inicio) {
      return new Date().toISOString().split('T')[0];
    }
    return nuevaSolicitud.fecha_inicio;
  };

  const formatearFechaRango = (fechaInicio, fechaFin) => {
    const inicio = new Date(fechaInicio);
    const fin = new Date(fechaFin);
    const opcionesInicio = { day: 'numeric', month: 'long' };
    const opcionesFin = { day: 'numeric', month: 'long', year: 'numeric' };
    return `${inicio.toLocaleDateString('es-ES', opcionesInicio)} - ${fin.toLocaleDateString('es-ES', opcionesFin)}`;
  };

  if (loading) {
    return (
      <div className="gestor-solicitudes-loading">
        <div className="spinner"></div>
        <p>Cargando solicitudes...</p>
      </div>
    );
  }

  return (
    <div className="gestor-mis-solicitudes">
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', color: '#336385' }}>
            Mis Solicitudes de Vacaciones
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gestiona tus días de vacaciones
          </Typography>
        </Box>
        <Tooltip title={!resumen.tiene_calendario_laboral ? 'No tienes calendario laboral asignado' : ''}>
          <span>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              disabled={!resumen.tiene_calendario_laboral}
              onClick={() => {
                setModoEdicion(false);
                setSolicitudEditando(null);
                setNuevaSolicitud({
                  fecha_inicio: '',
                  fecha_fin: '',
                  usar_dias_anio_anterior: false,
                  comentarios: ''
                });
                setShowModal(true);
              }}
              sx={{ backgroundColor: '#336385', '&:hover': { backgroundColor: '#27496d' } }}
            >
              Nueva Solicitud
            </Button>
          </span>
        </Tooltip>
      </Box>

      {!resumen.tiene_calendario_laboral && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          No tienes un calendario laboral asignado. Contacta con Recursos Humanos para que te asignen uno antes de poder solicitar vacaciones.
        </Alert>
      )}

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="h3" sx={{ color: '#22c55e', fontWeight: 'bold' }}>
                {resumen.dias_disponibles}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Días Disponibles
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="h3" sx={{ color: '#64748b', fontWeight: 'bold' }}>
                {resumen.dias_usados}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Días Usados ({anioSeleccionado})
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="h3" sx={{ color: '#f97316', fontWeight: 'bold' }}>
                {resumen.dias_verano_usados}/{resumen.dias_verano_total}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Días de Verano Obligatorios
              </Typography>
              {resumen.dias_verano_total - resumen.dias_verano_usados > 0 && (
                <Chip
                  icon={<BeachAccessIcon sx={{ fontSize: 16 }} />}
                  label={`Faltan ${resumen.dias_verano_total - resumen.dias_verano_usados} días`}
                  size="small"
                  sx={{ mt: 1, backgroundColor: '#fef3c7', color: '#92400e' }}
                />
              )}
            </CardContent>
          </Card>
        </Grid>

        {resumen.dias_pendientes_anio_anterior > 0 && resumen.dentro_periodo_gracia && (
          <Grid item xs={12}>
            <Alert
              severity="info"
              icon={<HistoryIcon />}
              sx={{ borderRadius: 2 }}
            >
              Tienes <strong>{resumen.dias_pendientes_anio_anterior} días</strong> pendientes del año anterior.
              Disponibles hasta el <strong>{new Date(resumen.fecha_limite_dias_pendientes).toLocaleDateString('es-ES')}</strong>.
            </Alert>
          </Grid>
        )}
      </Grid>

      <Card sx={{ borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#334155' }}>
              Historial de Solicitudes
            </Typography>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={anioSeleccionado}
                onChange={(e) => setAnioSeleccionado(e.target.value)}
                startAdornment={<CalendarMonthIcon sx={{ mr: 1, color: '#64748b' }} />}
                sx={{ borderRadius: 2 }}
              >
                {aniosDisponibles.map(anio => (
                  <MenuItem key={anio} value={anio}>{anio}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {solicitudes.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <EventBusyIcon sx={{ fontSize: 60, color: '#cbd5e1', mb: 2 }} />
              <Typography color="text.secondary">
                No tienes solicitudes de vacaciones en {anioSeleccionado}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {solicitudes.map((solicitud) => (
                <Box
                  key={solicitud.Id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    '&:hover': { backgroundColor: '#f1f5f9' }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        backgroundColor: solicitud.usar_dias_anio_anterior ? '#fee2e2' :
                                        solicitud.es_periodo_verano ? '#ffedd5' : '#e0f2fe',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {solicitud.usar_dias_anio_anterior ? (
                        <HistoryIcon sx={{ color: '#dc2626' }} />
                      ) : solicitud.es_periodo_verano ? (
                        <BeachAccessIcon sx={{ color: '#ea580c' }} />
                      ) : (
                        <EventAvailableIcon sx={{ color: '#0284c7' }} />
                      )}
                    </Box>
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 500, color: '#1e293b' }}>
                        {formatearFechaRango(solicitud.fecha_inicio, solicitud.fecha_fin)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {solicitud.dias_solicitados} días
                        {solicitud.dias_verano > 0 && solicitud.dias_verano < solicitud.dias_solicitados &&
                          ` (${solicitud.dias_solicitados - solicitud.dias_verano} normal + ${solicitud.dias_verano} verano)`
                        }
                      </Typography>
                      {solicitud.comentarios && (
                        <Typography variant="body2" sx={{ color: '#64748b', fontStyle: 'italic', mt: 0.5 }}>
                          <b>Comentario:</b> {solicitud.comentarios}
                        </Typography>
                      )}
                      {solicitud.estado === 'rechazada' && solicitud.motivo_rechazo && (
                        <Typography variant="body2" sx={{ color: '#dc2626', mt: 0.5, fontWeight: 500 }}>
                          <b>Motivo:</b> {solicitud.motivo_rechazo}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getEstadoChip(solicitud.estado)}
                    {puedeEditar(solicitud) && (
                      <Tooltip title={solicitud.estado === 'aprobada' ? 'Editar (volverá a pendiente)' : 'Editar'}>
                        <IconButton
                          size="small"
                          onClick={() => handleEditar(solicitud)}
                          sx={{ color: '#336385' }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {puedeEliminar(solicitud) && (
                      <Tooltip title="Eliminar">
                        <IconButton
                          size="small"
                          onClick={() => handleEliminar(solicitud)}
                          sx={{ color: '#dc2626' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      <Dialog open={showModal} onClose={handleCerrarModal} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#336385', color: 'white' }}>
          {modoEdicion ? 'Editar Solicitud de Vacaciones' : 'Nueva Solicitud de Vacaciones'}
        </DialogTitle>
        <form onSubmit={modoEdicion ? handleGuardarEdicion : handleCrearSolicitud}>
          <DialogContent>
            <TextField
              fullWidth
              margin="normal"
              label="Fecha Inicio"
              type="date"
              value={nuevaSolicitud.fecha_inicio}
              onChange={(e) => {
                const nuevaFechaInicio = e.target.value;
                const fechaFinActual = nuevaSolicitud.fecha_fin;
                if (fechaFinActual && fechaFinActual < nuevaFechaInicio) {
                  setNuevaSolicitud({ ...nuevaSolicitud, fecha_inicio: nuevaFechaInicio, fecha_fin: '' });
                } else {
                  setNuevaSolicitud({ ...nuevaSolicitud, fecha_inicio: nuevaFechaInicio });
                }
              }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: new Date().toISOString().split('T')[0] }}
              required
            />
            <TextField
              fullWidth
              margin="normal"
              label="Fecha Fin"
              type="date"
              value={nuevaSolicitud.fecha_fin}
              onChange={(e) => setNuevaSolicitud({ ...nuevaSolicitud, fecha_fin: e.target.value })}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: getFechaMinFin() }}
              required
            />
            {nuevaSolicitud.fecha_inicio && nuevaSolicitud.fecha_fin && (() => {
              const desglose = calcularDiasDesglose();
              return (
                <Box sx={{ my: 2, p: 2, backgroundColor: desglose.verano > 0 ? '#ffedd5' : '#e3f2fd', borderRadius: 1 }}>
                  <Typography variant="body1">
                    <strong>Días laborables solicitados:</strong> {desglose.total}
                  </Typography>
                  {desglose.verano > 0 && desglose.normales > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 2 }}>
                      <Typography variant="body2" sx={{ color: '#0284c7', fontWeight: 500 }}>
                        {desglose.normales} día{desglose.normales !== 1 ? 's' : ''} normal{desglose.normales !== 1 ? 'es' : ''}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <BeachAccessIcon sx={{ color: '#ea580c', mr: 0.5, fontSize: 18 }} />
                        <Typography variant="body2" sx={{ color: '#ea580c', fontWeight: 500 }}>
                          {desglose.verano} día{desglose.verano !== 1 ? 's' : ''} de verano
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  {desglose.verano > 0 && desglose.normales === 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <BeachAccessIcon sx={{ color: '#ea580c', mr: 1 }} />
                      <Typography variant="body2" sx={{ color: '#ea580c', fontWeight: 500 }}>
                        Periodo de verano
                      </Typography>
                    </Box>
                  )}
                </Box>
              );
            })()}

            {resumen.dias_pendientes_anio_anterior > 0 && resumen.dentro_periodo_gracia && (
              <Box sx={{ mt: 2, mb: 1 }}>
                <Alert
                  severity="info"
                  icon={<HistoryIcon />}
                  sx={{ mb: 1 }}
                >
                  Tienes <strong>{resumen.dias_pendientes_anio_anterior} días</strong> pendientes del año anterior.
                  Disponibles hasta el <strong>{new Date(resumen.fecha_limite_dias_pendientes).toLocaleDateString('es-ES')}</strong>.
                </Alert>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={nuevaSolicitud.usar_dias_anio_anterior}
                      onChange={(e) => setNuevaSolicitud({
                        ...nuevaSolicitud,
                        usar_dias_anio_anterior: e.target.checked
                      })}
                    />
                  }
                  label={`Usar días del año anterior (${resumen.dias_pendientes_anio_anterior} disponibles)`}
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontWeight: 500,
                      color: '#ff6b6b'
                    }
                  }}
                />
              </Box>
            )}
            <TextField
              fullWidth
              margin="normal"
              label="Comentarios"
              multiline
              rows={3}
              value={nuevaSolicitud.comentarios}
              onChange={(e) => setNuevaSolicitud({ ...nuevaSolicitud, comentarios: e.target.value })}
            />
          </DialogContent>
          {modoEdicion && solicitudEditando?.estado === 'aprobada' && (
            <Box sx={{ px: 3, pb: 2 }}>
              <Alert severity="warning">
                Al editar una solicitud aprobada, volverá al estado <strong>pendiente</strong> y requerirá nueva aprobación.
              </Alert>
            </Box>
          )}
          <DialogActions>
            <Button onClick={handleCerrarModal} sx={{ color: '#333' }}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{ backgroundColor: '#336385', '&:hover': { backgroundColor: '#27496d' } }}
            >
              {modoEdicion ? 'Guardar Cambios' : 'Crear Solicitud'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <ToastContainer />
    </div>
  );
}

export default GestorMisSolicitudes;
